import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

type Method = {
  id: string;
  label: string;
  icon: string;
  hint: string;
  needsRef: boolean;
  feeRate: number;
};

const METHODS: Method[] = [
  {
    id: "cash",
    label: "Cash",
    icon: "ri-cash-line",
    hint: "Crew lead collects cash on completion",
    needsRef: false,
    feeRate: 0,
  },
  {
    id: "check",
    label: "Check",
    icon: "ri-bill-line",
    hint: "Personal or business check made out to Haulmark",
    needsRef: true,
    feeRate: 0,
  },
  {
    id: "ach",
    label: "Bank Transfer (ACH)",
    icon: "ri-bank-line",
    hint: "Customer initiates ACH transfer to company account",
    needsRef: true,
    feeRate: 0,
  },
  {
    id: "card-offline",
    label: "Card (swiped on-site)",
    icon: "ri-bank-card-line",
    hint: "Crew lead runs the card through the mobile terminal",
    needsRef: false,
    feeRate: 0.029,
  },
];

type Props = {
  open: boolean;
  onClose: () => void;
  projectId: string;
  customer: { name: string; initials: string; color: string };
  balanceDue: number;
  onRecorded: (amount: number) => void;
};

export default function CollectBalanceModal({
  open,
  onClose,
  projectId,
  customer,
  balanceDue,
  onRecorded,
}: Props) {
  const [mode, setMode] = useState<"link" | "manual">("manual");
  const [methodId, setMethodId] = useState<string>("cash");
  const [amount, setAmount] = useState<string>(balanceDue.toString());
  const [reference, setReference] = useState("");
  const [state, setState] = useState<"idle" | "saving" | "success" | "error">(
    "idle",
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setMode("manual");
    setMethodId("cash");
    setAmount(balanceDue.toString());
    setReference("");
    setState("idle");
    setError(null);
  }, [open, balanceDue]);

  if (!open) return null;

  const method = METHODS.find((m) => m.id === methodId)!;
  const numericAmount = Number(amount);
  const amountValid = !Number.isNaN(numericAmount) && numericAmount > 0;
  const overpaying = numericAmount > balanceDue + 0.01;
  const fee = amountValid ? Math.round(numericAmount * method.feeRate * 100) / 100 : 0;

  const close = () => {
    if (state === "saving") return;
    onClose();
  };

  const formatDateLabel = () => {
    const d = new Date();
    const month = d.toLocaleString("en-US", { month: "short" });
    const day = String(d.getDate()).padStart(2, "0");
    const time = d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
    return `${month} ${day}, ${time}`;
  };

  const handleRecord = async () => {
    if (!amountValid) {
      setError("Enter a valid amount greater than zero.");
      return;
    }
    if (method.needsRef && !reference.trim()) {
      setError("Reference number is required for this method.");
      return;
    }
    setState("saving");
    setError(null);

    const paymentId = `PMT-${Math.floor(30500 + Math.random() * 9000)}`;
    const last4 =
      method.id === "check"
        ? reference.replace(/\D/g, "").slice(-4) || "—"
        : method.id === "ach"
        ? reference.slice(-4) || "—"
        : "—";

    const { error: insertError } = await supabase.from("admin_payments").insert({
      id: paymentId,
      project_id: projectId,
      customer: customer.name,
      initials: customer.initials,
      color: customer.color,
      amount: numericAmount,
      type: "balance",
      method: method.label,
      method_icon: method.icon,
      last4,
      status: "succeeded",
      date: formatDateLabel(),
      fee,
    });

    if (insertError) {
      setError(insertError.message);
      setState("error");
      return;
    }

    setState("success");
    setTimeout(() => {
      onRecorded(numericAmount);
      onClose();
      setState("idle");
    }, 1100);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={close}
    >
      <div
        className="bg-white rounded-xl w-full max-w-lg max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 pt-5 pb-4 border-b border-ink-100 flex items-start justify-between">
          <div>
            <h3 className="font-display text-lg font-bold text-ink-900">
              Collect balance
            </h3>
            <p className="text-xs text-ink-500 mt-0.5">
              {customer.name} · Project {projectId}
            </p>
          </div>
          <button
            onClick={close}
            className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-cream-100 text-ink-500 cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Balance summary */}
        <div className="px-5 pt-4">
          <div className="bg-ink-900 text-white rounded-lg p-4">
            <div className="text-[10px] font-bold uppercase tracking-wider text-white/60">
              Balance due
            </div>
            <div className="font-display text-3xl font-bold tabular-nums mt-1">
              ${balanceDue.toLocaleString()}
            </div>
            <div className="text-xs text-white/70 mt-1">
              Final payment owed by {customer.name.split(" ")[0]}
            </div>
          </div>
        </div>

        {/* Mode toggle */}
        <div className="px-5 pt-4">
          <div className="inline-flex bg-cream-100 rounded-md p-1 w-full">
            <button
              onClick={() => setMode("manual")}
              className={`flex-1 text-xs font-bold px-3 py-1.5 rounded-md cursor-pointer whitespace-nowrap transition-colors ${
                mode === "manual"
                  ? "bg-white text-ink-900 shadow-sm"
                  : "text-ink-500 hover:text-ink-700"
              }`}
            >
              <i className="ri-receipt-line mr-1"></i>
              Record payment
            </button>
            <button
              onClick={() => setMode("link")}
              className={`flex-1 text-xs font-bold px-3 py-1.5 rounded-md cursor-pointer whitespace-nowrap transition-colors ${
                mode === "link"
                  ? "bg-white text-ink-900 shadow-sm"
                  : "text-ink-500 hover:text-ink-700"
              }`}
            >
              <i className="ri-links-line mr-1"></i>
              Send payment link
            </button>
          </div>
        </div>

        {/* Body */}
        {mode === "manual" ? (
          <div className="p-5 space-y-4">
            {/* Method picker */}
            <div>
              <div className="text-[10px] uppercase tracking-wider text-ink-400 font-bold mb-2">
                Payment method
              </div>
              <div className="grid grid-cols-2 gap-2">
                {METHODS.map((m) => {
                  const active = m.id === methodId;
                  return (
                    <button
                      key={m.id}
                      onClick={() => setMethodId(m.id)}
                      className={`text-left p-3 rounded-lg border cursor-pointer transition-colors ${
                        active
                          ? "border-ink-900 bg-ink-900/5"
                          : "border-ink-100 hover:border-ink-300 bg-white"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className={`w-7 h-7 rounded-md flex items-center justify-center text-base ${
                            active
                              ? "bg-ink-900 text-white"
                              : "bg-cream-100 text-ink-700"
                          }`}
                        >
                          <i className={m.icon}></i>
                        </span>
                        <span className="text-xs font-bold text-ink-900">
                          {m.label}
                        </span>
                      </div>
                      <div className="text-[10px] text-ink-500 mt-1.5 leading-snug">
                        {m.hint}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Amount */}
            <div>
              <label className="text-[10px] uppercase tracking-wider text-ink-400 font-bold mb-1 block">
                Amount collected
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500 text-sm font-semibold">
                  $
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full text-sm pl-7 pr-3 py-2 rounded-md border border-ink-200 focus:border-ink-400 outline-none bg-white tabular-nums"
                />
              </div>
              <div className="flex items-center justify-between mt-1">
                <button
                  type="button"
                  onClick={() => setAmount(balanceDue.toString())}
                  className="text-[10px] font-semibold text-brand-600 hover:text-brand-700 cursor-pointer"
                >
                  Fill full balance (${balanceDue.toLocaleString()})
                </button>
                {overpaying && (
                  <span className="text-[10px] text-amber-600 font-semibold">
                    Over balance — will be recorded as overpayment
                  </span>
                )}
              </div>
            </div>

            {/* Reference */}
            {method.needsRef && (
              <div>
                <label className="text-[10px] uppercase tracking-wider text-ink-400 font-bold mb-1 block">
                  {method.id === "check" ? "Check number" : "Reference / confirmation"}
                </label>
                <input
                  type="text"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder={
                    method.id === "check" ? "e.g. 1042" : "e.g. ACH-9981"
                  }
                  className="w-full text-sm px-3 py-2 rounded-md border border-ink-200 focus:border-ink-400 outline-none bg-white"
                />
              </div>
            )}

            {/* Fee preview */}
            {amountValid && fee > 0 && (
              <div className="bg-cream-50 border border-ink-100 rounded-md p-3 text-xs flex items-center justify-between">
                <span className="text-ink-600">
                  Estimated processing fee (2.9%)
                </span>
                <span className="font-bold text-ink-900 tabular-nums">
                  ${fee.toFixed(2)}
                </span>
              </div>
            )}

            {error && (
              <div className="bg-rose-50 border border-rose-200 text-rose-700 text-[11px] rounded-md p-2.5 leading-relaxed">
                <span className="font-semibold">Couldn't record:</span> {error}
              </div>
            )}
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div className="bg-gradient-to-br from-indigo-50 to-brand-50 border border-ink-100 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-lg bg-white flex items-center justify-center text-indigo-600 text-xl border border-ink-100">
                  <i className="ri-secure-payment-line"></i>
                </span>
                <div className="flex-1">
                  <div className="text-sm font-bold text-ink-900">
                    Send a Stripe payment link
                  </div>
                  <div className="text-xs text-ink-600 mt-0.5 leading-relaxed">
                    Email or text {customer.name.split(" ")[0]} a secure
                    one-click checkout page. Card, Apple Pay, and Google Pay all
                    supported. Funds settle in 2 business days.
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs leading-relaxed">
              <div className="font-bold text-amber-900 flex items-center gap-1.5 mb-1">
                <i className="ri-error-warning-line"></i>
                Stripe isn't connected yet
              </div>
              <div className="text-amber-800">
                Connect Stripe in workspace settings to start sending checkout
                links to customers. Once connected, links will be generated
                automatically when you click below.
              </div>
            </div>

            <ul className="space-y-2 text-xs text-ink-700">
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-emerald-600 mt-0.5"></i>
                <span>Customer pays from any device, no login required</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-emerald-600 mt-0.5"></i>
                <span>Receipt auto-emailed once payment clears</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-emerald-600 mt-0.5"></i>
                <span>Payment status syncs back to this project automatically</span>
              </li>
            </ul>
          </div>
        )}

        {/* Footer */}
        <div className="px-5 pb-5 pt-3 border-t border-ink-100 flex items-center justify-between gap-2 bg-cream-50 rounded-b-xl">
          <button
            onClick={close}
            disabled={state === "saving"}
            className="text-sm font-semibold text-ink-600 hover:text-ink-900 px-3 py-2 cursor-pointer whitespace-nowrap disabled:cursor-not-allowed"
          >
            Cancel
          </button>

          {mode === "manual" ? (
            <button
              onClick={handleRecord}
              disabled={state === "saving" || state === "success" || !amountValid}
              className={`inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-md cursor-pointer whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                state === "success"
                  ? "bg-emerald-500 text-white"
                  : "bg-ink-900 hover:bg-ink-800 text-white"
              }`}
            >
              <i
                className={
                  state === "saving"
                    ? "ri-loader-4-line animate-spin"
                    : state === "success"
                    ? "ri-check-double-line"
                    : "ri-check-line"
                }
              ></i>
              {state === "saving"
                ? "Recording…"
                : state === "success"
                ? "Recorded"
                : `Record $${amountValid ? numericAmount.toLocaleString() : "0"}`}
            </button>
          ) : (
            <button
              disabled
              className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-md bg-ink-200 text-ink-500 cursor-not-allowed whitespace-nowrap"
            >
              <i className="ri-links-line"></i>
              Connect Stripe to send link
            </button>
          )}
        </div>
      </div>
    </div>
  );
}